import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useSearchParams } from 'react-router-dom';
import { todayLocal, localDateOffset } from '../lib/dateHelpers';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import StatTile from '../components/StatTile';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TimeInput from '../components/TimeInput';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Search,
  Mail,
  AlertTriangle,
  CheckCircle,
  FileText,
  RefreshCw,
  Filter,
  X,
  MapPin,
  Building,
  Calendar,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Eye,
  MessageSquare,
  Clock,
  Trash2,
  Database,
  CheckSquare,
  Square,
  Bell,
  Ban,
  Unlock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Pagination bar — page numbers + prev/next. Rendered above AND below
 * the list. Shows a windowed range (e.g. "1 … 4 5 [6] 7 8 … 14") so the
 * bar never gets wider than the card.
 *
 * Props:
 *   page        — current 1-indexed page
 *   pageSize    — items per page (50 here)
 *   total       — total result count
 *   onPageChange(newPage)
 */
function Pagination({ page, pageSize, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (totalPages <= 1) return null;

  // Build the windowed page list: always show first, last, current, and
  // 1 neighbour each side. Gaps become "…".
  const pages = [];
  const window = new Set([1, totalPages, page - 1, page, page + 1]);
  for (let p = 1; p <= totalPages; p++) {
    if (window.has(p)) pages.push(p);
  }
  const display = [];
  let prev = 0;
  for (const p of pages) {
    if (p - prev > 1) display.push('…');
    display.push(p);
    prev = p;
  }

  const firstShown = (page - 1) * pageSize + 1;
  const lastShown = Math.min(page * pageSize, total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 py-3"
      data-testid="tickets-pagination-bar"
    >
      <div className="text-xs text-slate-500" data-testid="tickets-pagination-summary">
        Showing <span className="font-semibold text-slate-700">{firstShown}–{lastShown}</span> of{' '}
        <span className="font-semibold text-slate-700">{total}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          data-testid="tickets-page-prev"
          className="h-8 px-2"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {display.map((item, idx) =>
          item === '…' ? (
            <span key={`gap-${idx}`} className="px-2 text-slate-400 text-sm">…</span>
          ) : (
            <Button
              key={item}
              variant={item === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(item)}
              data-testid={`tickets-page-${item}`}
              className="h-8 min-w-[2rem] px-2"
            >
              {item}
            </Button>
          )
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          data-testid="tickets-page-next"
          className="h-8 px-2"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function Tickets811() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRemindersOnly, setShowRemindersOnly] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [filters, setFilters] = useState({
    ticket_type: '',
    priority: '',
    utility_company: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Handle URL parameter for reminders filter
  useEffect(() => {
    if (searchParams.get('filter') === 'reminders') {
      setShowRemindersOnly(true);
    }
  }, [searchParams]);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [reminderTicket, setReminderTicket] = useState(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('09:00');
  const [reminderNote, setReminderNote] = useState('');
  const [savingReminder, setSavingReminder] = useState(false);
  const [blockedEmails, setBlockedEmails] = useState([]);
  const [showBlockedEmails, setShowBlockedEmails] = useState(false);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [unblocking, setUnblocking] = useState(null);
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: '', filename: '', contentType: '', blobUrl: '', loading: false, error: '' });

  // Fetch PDF via proxy and create blob URL for viewing
  const fetchPdfBlob = useCallback(async (url, filename, contentType) => {
    setPdfViewer({ open: true, url, filename, contentType, blobUrl: '', loading: true, error: '' });
    
    try {
      const response = await fetch(`${API}/api/tickets811/proxy-attachment?url=${encodeURIComponent(url)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfViewer(prev => ({ ...prev, blobUrl, loading: false }));
    } catch (error) {
      console.error('Failed to fetch PDF:', error);
      setPdfViewer(prev => ({ ...prev, loading: false, error: error.message }));
    }
  }, [token]);

  // Cleanup blob URL when viewer closes
  useEffect(() => {
    return () => {
      if (pdfViewer.blobUrl) {
        URL.revokeObjectURL(pdfViewer.blobUrl);
      }
    };
  }, [pdfViewer.blobUrl]);

  // Fetch tickets with search/filter
  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (filters.ticket_type) params.append('ticket_type', filters.ticket_type);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.utility_company) params.append('utility_company', filters.utility_company);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (showRemindersOnly) params.append('has_reminder', 'true');
      if (showUnreadOnly) params.append('unread_only', 'true');

      // Server-side pagination: 50 per page. Backend sorts by received_at DESC,
      // so page 1 is newest. `total` drives the page-number bar.
      params.append('limit', String(PAGE_SIZE));
      params.append('skip', String((page - 1) * PAGE_SIZE));

      const response = await fetch(`${API}/api/tickets811?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTickets(data.tickets || []);
      setTotalTickets(typeof data.total === 'number' ? data.total : 0);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load 811 tickets');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, filters, showRemindersOnly, showUnreadOnly, page]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/tickets811/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/tickets811/sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSyncStatus(data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  }, [token]);

  // Fetch blocked emails
  const fetchBlockedEmails = useCallback(async () => {
    setLoadingBlocked(true);
    try {
      const response = await fetch(`${API}/api/tickets811/blocked-emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setBlockedEmails(data.blocked_emails || []);
    } catch (error) {
      console.error('Error fetching blocked emails:', error);
      toast.error('Failed to load blocked emails');
    } finally {
      setLoadingBlocked(false);
    }
  }, [token]);

  // Unblock an email
  const unblockEmail = async (emailId) => {
    setUnblocking(emailId);
    try {
      const response = await fetch(`${API}/api/tickets811/blocked-emails/${encodeURIComponent(emailId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Email unblocked - it will sync again on next refresh');
        setBlockedEmails(prev => prev.filter(b => b.email_id !== emailId));
      } else {
        toast.error('Failed to unblock email');
      }
    } catch (error) {
      console.error('Error unblocking email:', error);
      toast.error('Failed to unblock email');
    } finally {
      setUnblocking(null);
    }
  };

  // Refresh all data
  const refreshAllData = useCallback(() => {
    fetchTickets();
    fetchStats();
    fetchSyncStatus();
  }, [fetchTickets, fetchStats, fetchSyncStatus]);

  useEffect(() => {
    refreshAllData();
  }, [refreshAllData]);

  // Auto-refresh when user returns to app
  useAutoRefresh(refreshAllData, { refreshOnReturn: true });

  // Reset to page 1 whenever search/filter changes (so results don't
  // appear "empty" if the user was on page 5 of a now-shorter result set).
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filters, showRemindersOnly, showUnreadOnly]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTickets();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filters, fetchTickets]);

  // Seed sample data
  const seedSampleData = async () => {
    try {
      const response = await fetch(`${API}/api/tickets811/seed-sample-data`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to seed sample data');
    }
  };

  // Test Gmail connection
  const testGmailConnection = async () => {
    try {
      const response = await fetch(`${API}/api/tickets811/test-gmail-connection`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setGmailStatus(data);
      if (data.success) {
        toast.success(`Connected to ${data.email} (${data.inbox_count} emails)`);
      } else {
        toast.error(data.error || 'Gmail connection failed');
      }
    } catch (error) {
      toast.error('Failed to test Gmail connection');
    }
  };

  // Refresh data from database
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchTickets(), fetchStats(), fetchSyncStatus()]);
      toast.success('Refreshed');
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  // Sync emails from Gmail
  const syncFromGmail = async () => {
    setSyncing(true);
    try {
      // Try 811 IMAP first (if configured), fall back to Gmail.
      // No body params: backend auto-catches-up via IMAP SEARCH SINCE
      // (newest received_at in db.tickets_811 - 24h overlap). Dedup
      // makes this idempotent. See routes/tickets811.py::import_811_emails.
      const response = await fetch(`${API}/api/tickets811/import-811-emails`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        const since = data.search_since ? ` since ${data.search_since}` : '';
        const message = `Imported ${data.imported} new tickets${since} (${data.duplicates} duplicates, ${data.fetched} scanned)`;
        toast.success(message);
        fetchTickets();
        fetchStats();
        setSyncing(false);
      } else if (response.status === 400 && data.detail?.includes('credentials not configured')) {
        // Fall back to Gmail sync if 811 email not configured
        const gmailResponse = await fetch(`${API}/api/tickets811/sync-gmail`, {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ limit: 20 })
        });
        const gmailData = await gmailResponse.json();
        
        if (gmailData.success) {
          if (gmailData.status === 'syncing') {
            toast.success('Sync started! Refreshing in a few seconds...');
            setTimeout(() => {
              fetchTickets();
              fetchStats();
              fetchSyncStatus();
            }, 5000);
            setTimeout(() => {
              fetchTickets();
              fetchStats();
              fetchSyncStatus();
              setSyncing(false);
            }, 15000);
          } else {
            const message = gmailData.filtered 
              ? `Synced ${gmailData.inserted} new emails (${gmailData.duplicates} duplicates, ${gmailData.filtered} filtered)`
              : `Synced ${gmailData.inserted} new emails (${gmailData.duplicates} duplicates skipped)`;
            toast.success(message);
            fetchTickets();
            fetchStats();
            setSyncing(false);
          }
        } else {
          toast.error(gmailData.detail || 'Sync failed');
          setSyncing(false);
        }
      } else {
        toast.error(data.detail || 'Sync failed');
        setSyncing(false);
      }
    } catch (error) {
      toast.error('Failed to sync emails');
      setSyncing(false);
    }
  };

  // Initial import of 811 emails (first 75)
  const initialImport811 = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/api/tickets811/import-811-emails`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ initial_import: true, limit: 75 })
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Initial import complete! Imported ${data.imported} tickets (${data.duplicates} duplicates skipped)`);
        fetchTickets();
        fetchStats();
      } else {
        toast.error(data.detail || 'Initial import failed');
      }
    } catch (error) {
      toast.error('Failed to import emails');
    } finally {
      setSyncing(false);
    }
  };

  // Open ticket detail
  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setNotes(ticket.notes || '');
    setShowTicketDialog(true);
    
    // Mark as read
    if (!ticket.is_read) {
      try {
        await fetch(`${API}/api/tickets811/${ticket.id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setTickets(prev => prev.map(t => 
          t.id === ticket.id ? { ...t, is_read: true } : t
        ));
        fetchStats();
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  // Save notes
  const saveNotes = async () => {
    if (!selectedTicket) return;
    setSavingNotes(true);
    try {
      await fetch(`${API}/api/tickets811/${selectedTicket.id}/notes`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes })
      });
      toast.success('Notes saved');
      setTickets(prev => prev.map(t =>
        t.id === selectedTicket.id ? { ...t, notes } : t
      ));
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Delete ticket
  const deleteTicket = async (ticketId) => {
    console.log('deleteTicket called with:', ticketId);
    setDeleting(true);
    try {
      const response = await fetch(`${API}/api/tickets811/${ticketId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Delete response:', response.status);
      if (response.ok) {
        toast.success('Ticket deleted');
        setShowTicketDialog(false);
        setSelectedTicket(null);
        fetchTickets();
        fetchStats();
      } else {
        toast.error('Failed to delete ticket');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete tickets
  const bulkDeleteTickets = async () => {
    if (selectedTickets.size === 0) {
      toast.error('No tickets selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedTickets.size} ticket(s)? This cannot be undone.`)) {
      return;
    }
    
    setBulkDeleting(true);
    try {
      const response = await fetch(`${API}/api/tickets811/bulk-delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ticket_ids: Array.from(selectedTickets) })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Deleted ${data.deleted_count} ticket(s)`);
        setSelectedTickets(new Set());
        fetchTickets();
        fetchStats();
      } else {
        toast.error('Failed to delete tickets');
      }
    } catch (error) {
      toast.error('Failed to delete tickets');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Toggle ticket selection
  const toggleTicketSelection = (ticketId) => {
    const newSelected = new Set(selectedTickets);
    if (newSelected.has(ticketId)) {
      newSelected.delete(ticketId);
    } else {
      newSelected.add(ticketId);
    }
    setSelectedTickets(newSelected);
  };

  // Select all visible tickets
  const selectAllTickets = () => {
    if (selectedTickets.size === tickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map(t => t.id)));
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ ticket_type: '', priority: '', utility_company: '', date_from: '', date_to: '' });
    setSearchQuery('');
  };

  // Open reminder dialog
  const openReminderDialog = (ticket) => {
    setReminderTicket(ticket);
    // Set default date to tomorrow (in user's local timezone — Eastern for this company)
    setReminderDate(localDateOffset(1));
    setReminderTime('09:00');
    setReminderNote(ticket.reminder_note || '');
    setShowReminderDialog(true);
  };

  // Save reminder
  const saveReminder = async () => {
    if (!reminderTicket || !reminderDate) return;
    
    setSavingReminder(true);
    try {
      const reminderAt = new Date(`${reminderDate}T${reminderTime}`).toISOString();
      
      await fetch(`${API}/api/tickets811/${reminderTicket.id}/reminder`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reminder_at: reminderAt,
          reminder_note: reminderNote
        })
      });
      
      toast.success('Reminder set!');
      setShowReminderDialog(false);
      setReminderTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to set reminder');
    } finally {
      setSavingReminder(false);
    }
  };

  // Clear reminder
  const clearReminder = async () => {
    if (!reminderTicket) return;
    
    setSavingReminder(true);
    try {
      await fetch(`${API}/api/tickets811/${reminderTicket.id}/reminder`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reminder cleared');
      setShowReminderDialog(false);
      setReminderTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to clear reminder');
    } finally {
      setSavingReminder(false);
    }
  };

  // Get priority badge color
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return <Badge className="bg-red-600 text-white">Critical</Badge>;
      case 'high_priority':
        return <Badge className="bg-orange-500 text-white">High Priority</Badge>;
      default:
        return <Badge variant="secondary">Normal</Badge>;
    }
  };

  // Get ticket type badge
  const getTypeBadge = (type) => {
    if (type === 'utility_advisory') {
      return <Badge className="bg-purple-600 text-white">Advisory</Badge>;
    }
    return <Badge className="bg-blue-600 text-white">811 Ticket</Badge>;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v).length + (searchQuery ? 1 : 0);

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            811 Info
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Search and view 811 tickets and utility advisories
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant={showRemindersOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowRemindersOnly(!showRemindersOnly)}
            className={`h-9 gap-2 ${showRemindersOnly ? 'bg-slate-800 text-white hover:bg-slate-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            data-testid="show-reminders-btn"
          >
            <Bell className="w-4 h-4" />
            Reminders
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-9 gap-2 border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            size="sm"
            onClick={syncFromGmail}
            disabled={syncing}
            className="h-9 gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
            data-testid="sync-gmail-btn"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      {/* Sync Status Banner */}
      {syncStatus && (
        <div className="flex flex-wrap items-center gap-4 p-3 bg-slate-50 rounded-lg text-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">Last sync:</span>
            <span className="font-medium">{formatTimeAgo(syncStatus.last_sync)}</span>
          </div>
          {syncStatus.last_sync_result && (
            <div className="flex items-center gap-2">
              <span className="text-slate-400">|</span>
              <span className="text-green-600">+{syncStatus.last_sync_result.inserted} new</span>
              <span className="text-slate-400">({syncStatus.last_sync_result.duplicates} skipped)</span>
              {syncStatus.last_sync_result.filtered > 0 && (
                <span className="text-orange-600">({syncStatus.last_sync_result.filtered} filtered)</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reminders Filter Banner */}
      {showRemindersOnly && (
        <div className="flex items-center justify-between p-3 bg-green-100 border border-green-300 rounded-lg">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Showing tickets with reminders only</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowRemindersOnly(false)}
            className="text-green-700 hover:text-green-900 hover:bg-green-200"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Filter
          </Button>
        </div>
      )}

      {/* Blocked Emails Section */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 cursor-pointer" onClick={() => {
          if (!showBlockedEmails) {
            fetchBlockedEmails();
          }
          setShowBlockedEmails(!showBlockedEmails);
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-slate-500" />
              <CardTitle className="text-lg font-['Barlow_Condensed']">
                Blocked Emails
              </CardTitle>
              {blockedEmails.length > 0 && (
                <Badge variant="secondary" className="ml-1">{blockedEmails.length}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {showBlockedEmails ? 'Click to hide' : 'Click to view'}
              </span>
              {showBlockedEmails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </CardHeader>
        {showBlockedEmails && (
          <CardContent className="pt-0">
            {loadingBlocked ? (
              <div className="flex items-center justify-center py-6">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : blockedEmails.length === 0 ? (
              <div className="text-center py-6">
                <Ban className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No blocked emails</p>
                <p className="text-xs text-slate-400 mt-1">
                  Deleted tickets are automatically blocked from re-syncing
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <p className="text-xs text-slate-500 mb-3">
                  These emails are blocked from syncing. Unblock to allow them to sync again.
                </p>
                {blockedEmails.map((blocked) => (
                  <div
                    key={blocked.email_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                    data-testid={`blocked-email-${blocked.email_id}`}
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {blocked.subject || 'No subject'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">
                          Blocked: {blocked.blocked_at ? new Date(blocked.blocked_at).toLocaleDateString() : 'Unknown'}
                        </span>
                        {blocked.reason && (
                          <Badge variant="outline" className="text-xs">
                            {blocked.reason === 'user_deleted' ? 'User deleted' : blocked.reason}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => unblockEmail(blocked.email_id)}
                      disabled={unblocking === blocked.email_id}
                      className="gap-1 flex-shrink-0"
                      data-testid={`unblock-btn-${blocked.email_id}`}
                    >
                      {unblocking === blocked.email_id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Unlock className="w-3 h-3" />
                      )}
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Total Tickets — click to clear all filters */}
          <Card
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:opacity-90 transition-all"
            onClick={() => {
              setFilters({ ticket_type: '', priority: '', utility_company: '', date_from: '', date_to: '' });
              setShowUnreadOnly(false);
              setShowRemindersOnly(false);
              setSearchQuery('');
            }}
            data-testid="stat-card-total"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Tickets</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Mail className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          {/* Unread — click to toggle showUnreadOnly */}
          <Card
            className={`bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:opacity-90 transition-all ${showUnreadOnly ? 'ring-4 ring-green-300' : ''}`}
            onClick={() => setShowUnreadOnly(prev => !prev)}
            data-testid="stat-card-unread"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Unread</p>
                  <p className="text-2xl font-bold">{stats.unread}</p>
                </div>
                <Eye className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          {/* High Priority — click to toggle priority filter */}
          <Card
            className={`bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:opacity-90 transition-all ${filters.priority === 'high_priority' ? 'ring-4 ring-red-300' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, priority: prev.priority === 'high_priority' ? '' : 'high_priority' }))}
            data-testid="stat-card-high-priority"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">High Priority</p>
                  <p className="text-2xl font-bold">{stats.high_priority}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`!bg-amber-400 text-white cursor-pointer hover:opacity-90 transition-all ${filters.priority === 'high_pressure_gas' ? 'ring-4 ring-amber-300' : ''}`} 
            onClick={() => setFilters(prev => ({ ...prev, priority: prev.priority === 'high_pressure_gas' ? '' : 'high_pressure_gas' }))}
            data-testid="stat-card-hp-gas"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">HP Gas</p>
                  <p className="text-2xl font-bold">{stats.high_pressure_gas || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-100" />
              </div>
            </CardContent>
          </Card>
          {/* Last 7 Days — click to set date range */}
          <Card
            className={`bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:opacity-90 transition-all ${(() => {
              if (!filters.date_from || !filters.date_to) return '';
              const today = todayLocal();
              const sevenAgo = localDateOffset(-7);
              return filters.date_from === sevenAgo && filters.date_to.startsWith(today) ? 'ring-4 ring-purple-300' : '';
            })()}`}
            onClick={() => {
              const today = todayLocal();
              const sevenAgo = localDateOffset(-7);
              const isAlready = filters.date_from === sevenAgo && filters.date_to.startsWith(today);
              setFilters(prev => ({
                ...prev,
                // date_to gets a time suffix so backend's lex-compare against full ISO timestamps
                // (e.g. "2026-05-07T10:30:00+00:00") still includes same-day tickets.
                date_from: isAlready ? '' : sevenAgo,
                date_to: isAlready ? '' : `${today}T23:59:59`,
              }));
            }}
            data-testid="stat-card-last-7-days"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Last 7 Days</p>
                  <p className="text-2xl font-bold">{stats.recent_7_days}</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search ticket #, address, city, utility, or any keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge className="bg-amber-500 text-white ml-1">{activeFiltersCount}</Badge>
              )}
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
                <Select
                  value={filters.ticket_type || "all"}
                  onValueChange={(v) => setFilters(f => ({ ...f, ticket_type: v === "all" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="811_ticket">811 Tickets</SelectItem>
                    <SelectItem value="utility_advisory">Utility Advisories</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
                <Select
                  value={filters.priority || "all"}
                  onValueChange={(v) => setFilters(f => ({ ...f, priority: v === "all" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Priorities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high_priority">High Priority</SelectItem>
                    <SelectItem value="high_pressure_gas">High Pressure Gas</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Utility Company</label>
                <Select
                  value={filters.utility_company || "all"}
                  onValueChange={(v) => setFilters(f => ({ ...f, utility_company: v === "all" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    <SelectItem value="AT&T">AT&T</SelectItem>
                    <SelectItem value="Lumen">Lumen/Level 3</SelectItem>
                    <SelectItem value="Duke Energy">Duke Energy</SelectItem>
                    <SelectItem value="Verizon">Verizon</SelectItem>
                    <SelectItem value="Comcast">Comcast</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Date From</label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Date To</label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Barlow_Condensed']">
              {searchQuery || activeFiltersCount > 0 ? 'Search Results' : 'All Tickets'}
              <span className="text-slate-500 font-normal ml-2" data-testid="tickets-list-count">
                ({totalTickets})
              </span>
            </CardTitle>
            {tickets.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllTickets}
                  className="gap-1"
                >
                  {selectedTickets.size === tickets.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedTickets.size === tickets.length ? 'Deselect All' : 'Select All'}
                </Button>
                {selectedTickets.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={bulkDeleteTickets}
                    disabled={bulkDeleting}
                    className="gap-1"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    {bulkDeleting ? 'Deleting...' : `Delete ${selectedTickets.size}`}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Top pagination bar */}
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={totalTickets}
            onPageChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No tickets found</p>
              {searchQuery && (
                <p className="text-sm text-slate-400 mt-1">
                  Try adjusting your search or filters
                </p>
              )}
              <Button
                variant="outline"
                className="mt-4"
                onClick={seedSampleData}
              >
                <Database className="w-4 h-4 mr-2" />
                Load Sample Data
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`p-3 sm:p-4 rounded-lg border transition-all hover:shadow-md overflow-hidden ${
                    !ticket.is_read ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200 hover:border-slate-300'
                  } ${selectedTickets.has(ticket.id) ? 'ring-2 ring-blue-500' : ''}`}
                  data-testid={`ticket-${ticket.id}`}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    {/* Checkbox */}
                    <div 
                      className="mt-1 cursor-pointer flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTicketSelection(ticket.id);
                      }}
                    >
                      {selectedTickets.has(ticket.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                      )}
                    </div>
                    
                    {/* Ticket Content */}
                    <div 
                      className="flex-1 cursor-pointer min-w-0 overflow-hidden"
                      onClick={() => openTicket(ticket)}
                    >
                      <div className="flex flex-col gap-1 sm:gap-2">
                        <div className="min-w-0 overflow-hidden">
                          <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 mb-1">
                            {!ticket.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                            )}
                            {getTypeBadge(ticket.ticket_type)}
                            {getPriorityBadge(ticket.priority)}
                            {ticket.has_high_pressure_gas && (
                              <Badge className="!bg-amber-400 text-white text-xs whitespace-nowrap">
                                High Pressure Gas
                              </Badge>
                            )}
                            {ticket.ticket_number && (
                              <Badge variant="outline" className="font-mono text-xs whitespace-nowrap">
                                #{ticket.ticket_number}
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-900 text-sm md:text-base line-clamp-2 break-words overflow-hidden" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {ticket.subject}
                          </h3>
                          <div className="flex flex-col gap-1 mt-2 text-xs sm:text-sm text-slate-600 overflow-hidden">
                            {ticket.address && (
                              <span className="flex items-start gap-1 overflow-hidden">
                                <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                <span className="line-clamp-1 break-words" style={{ wordBreak: 'break-word' }}>{ticket.address}{ticket.city ? `, ${ticket.city}` : ''}</span>
                              </span>
                            )}
                            {ticket.utility_company && (
                              <span className="flex items-center gap-1">
                                <Building className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{ticket.utility_company}</span>
                              </span>
                            )}
                            {ticket.reminder_at && (
                              <span className="flex items-center gap-1 text-green-700 font-medium bg-green-100 px-2 py-0.5 rounded-full w-fit">
                                <Bell className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">Reminder: {formatDate(ticket.reminder_at)}</span>
                              </span>
                            )}
                            {ticket.attachments?.length > 0 && (
                              <span className="flex items-center gap-1 text-purple-600">
                                <Paperclip className="w-3 h-3 flex-shrink-0" />
                                <span>{ticket.attachments.length} file{ticket.attachments.length > 1 ? 's' : ''}</span>
                              </span>
                            )}
                            {ticket.inline_images?.length > 0 && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <Eye className="w-3 h-3 flex-shrink-0" />
                                <span>{ticket.inline_images.length} img{ticket.inline_images.length > 1 ? 's' : ''}</span>
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {formatDate(ticket.received_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openReminderDialog(ticket);
                        }}
                        className="text-slate-400 hover:text-amber-600 p-1 h-auto"
                        title="Set reminder"
                        data-testid={`reminder-btn-${ticket.id}`}
                      >
                        <Bell className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Delete clicked for ticket:', ticket.id);
                          deleteTicket(ticket.id);
                        }}
                        className="text-slate-400 hover:text-red-600 p-1 h-auto"
                        title="Delete ticket"
                        data-testid={`delete-btn-${ticket.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Bottom pagination bar */}
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={totalTickets}
            onPageChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-slate-200">
            <DialogTitle className="flex items-start gap-3 font-['Barlow_Condensed'] text-lg leading-relaxed">
              <Mail className="w-5 h-5 mt-1 flex-shrink-0" />
              <span className="break-words">{selectedTicket?.subject}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4 pt-4">
              {/* Badges Row */}
              <div className="flex flex-wrap gap-2">
                {getTypeBadge(selectedTicket.ticket_type)}
                {getPriorityBadge(selectedTicket.priority)}
                {selectedTicket.has_high_pressure_gas && (
                  <Badge className="!bg-amber-400 text-white">
                    High Pressure Gas
                  </Badge>
                )}
                {selectedTicket.ticket_number && (
                  <Badge variant="outline" className="font-mono text-lg">
                    Ticket #{selectedTicket.ticket_number}
                  </Badge>
                )}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">From</p>
                  <p className="font-medium">{selectedTicket.sender}</p>
                  <p className="text-sm text-slate-600">{selectedTicket.sender_email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Received</p>
                  <p className="font-medium">{formatDate(selectedTicket.received_at)}</p>
                </div>
                {selectedTicket.address && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Location</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {selectedTicket.address}
                      {selectedTicket.city && `, ${selectedTicket.city}`}
                      {selectedTicket.state && ` ${selectedTicket.state}`}
                    </p>
                  </div>
                )}
                {selectedTicket.utility_company && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Utility Company</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building className="w-4 h-4 text-slate-400" />
                      {selectedTicket.utility_company}
                    </p>
                  </div>
                )}
                {selectedTicket.caller_name && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Caller</p>
                    <p className="font-medium">{selectedTicket.caller_name}</p>
                  </div>
                )}
              </div>

              {/* Email Body */}
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Email Content</p>
                <div className="p-4 bg-white border rounded-lg">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">
                    {selectedTicket.body_text}
                  </pre>
                </div>
              </div>

              {/* Attachments */}
              {selectedTicket.attachments?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" />
                    Attachments ({selectedTicket.attachments.length})
                  </p>
                  <div className="space-y-4">
                    {selectedTicket.attachments.map((att, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between gap-2 p-3 bg-purple-50 border-b">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-purple-600" />
                            <span className="font-medium text-purple-900">{att.filename}</span>
                            <span className="text-xs text-purple-600">
                              {att.size ? `(${(att.size / 1024).toFixed(1)} KB)` : ''}
                            </span>
                          </div>
                          {att.url && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-shrink-0 text-purple-700 border-purple-300 hover:bg-purple-100"
                              onClick={() => {
                                // For images, show directly. For PDFs, use proxy
                                if (att.content_type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(att.filename)) {
                                  setPdfViewer({ open: true, url: att.url, filename: att.filename, contentType: att.content_type, blobUrl: att.url, loading: false, error: '' });
                                } else {
                                  fetchPdfBlob(att.url, att.filename, att.content_type);
                                }
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                        </div>
                        {att.extracted_text && (
                          <div className="p-4 bg-white max-h-64 overflow-y-auto">
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Extracted Text (Searchable)</p>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 break-words" style={{ wordBreak: 'break-word' }}>
                              {att.extracted_text}
                            </pre>
                          </div>
                        )}
                        {!att.extracted_text && !att.url && (
                          <div className="p-4 bg-slate-50 text-center text-sm text-slate-500">
                            <Eye className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                            No text content extracted from this PDF
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Inline Images from Email */}
              {selectedTicket.inline_images?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    Email Images ({selectedTicket.inline_images.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedTicket.inline_images.map((img, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden bg-slate-50">
                        {img.url ? (
                          <a href={img.url} target="_blank" rel="noopener noreferrer" className="block">
                            <img 
                              src={img.url} 
                              alt={img.filename || `Image ${idx + 1}`}
                              className="w-full h-32 object-cover hover:opacity-90 transition-opacity"
                            />
                            <div className="p-2 text-xs text-center text-slate-600 truncate">
                              {img.filename || `Image ${idx + 1}`}
                            </div>
                          </a>
                        ) : (
                          <div className="h-32 flex items-center justify-center text-slate-400">
                            <Eye className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  Notes
                </p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this ticket..."
                  rows={3}
                />
                <div className="flex items-center justify-between mt-2">
                  <Button
                    size="sm"
                    onClick={saveNotes}
                    disabled={savingNotes}
                  >
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => deleteTicket(selectedTicket.id)}
                    disabled={deleting}
                    className="gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    {deleting ? 'Deleting...' : 'Delete Ticket'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Barlow_Condensed']">
              <Bell className="w-5 h-5 text-amber-500" />
              Set Reminder
            </DialogTitle>
          </DialogHeader>
          
          {reminderTicket && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-900 line-clamp-2">
                  {reminderTicket.subject}
                </p>
                {reminderTicket.ticket_number && (
                  <p className="text-xs text-slate-500 mt-1">
                    Ticket #{reminderTicket.ticket_number}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Date</label>
                  <Input
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Time</label>
                  <TimeInput
                    value={reminderTime}
                    onChange={(val) => setReminderTime(val)}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Note (optional)</label>
                <Textarea
                  value={reminderNote}
                  onChange={(e) => setReminderNote(e.target.value)}
                  placeholder="Add a note for this reminder..."
                  rows={2}
                />
              </div>
              
              <div className="flex items-center justify-between pt-2">
                {reminderTicket.reminder_at && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearReminder}
                    disabled={savingReminder}
                    className="text-red-600 hover:text-red-700"
                  >
                    Clear Reminder
                  </Button>
                )}
                <div className="flex gap-2 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReminderDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveReminder}
                    disabled={savingReminder || !reminderDate}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {savingReminder ? 'Saving...' : 'Set Reminder'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF/Image/File Viewer Dialog */}
      <Dialog open={pdfViewer.open} onOpenChange={(open) => {
        if (!open && pdfViewer.blobUrl && !pdfViewer.blobUrl.startsWith('http')) {
          URL.revokeObjectURL(pdfViewer.blobUrl);
        }
        setPdfViewer({ open: false, url: '', filename: '', contentType: '', blobUrl: '', loading: false, error: '' });
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              {pdfViewer.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-auto max-h-[70vh] flex items-center justify-center bg-slate-100 rounded-lg min-h-[300px]">
            {pdfViewer.loading ? (
              <div className="flex flex-col items-center gap-3 p-8">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
                <p className="text-slate-500">Loading document...</p>
              </div>
            ) : pdfViewer.error ? (
              <div className="flex flex-col items-center gap-3 p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-red-400" />
                <p className="text-slate-600 font-medium">Failed to load preview</p>
                <p className="text-sm text-slate-500">{pdfViewer.error}</p>
                <Button variant="outline" onClick={() => window.open(pdfViewer.url, '_blank')}>
                  Try downloading instead
                </Button>
              </div>
            ) : pdfViewer.blobUrl ? (
              // Check if it's an image by extension or content type
              /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(pdfViewer.filename) || 
              pdfViewer.contentType?.startsWith('image/') ? (
                <img 
                  src={pdfViewer.blobUrl} 
                  alt={pdfViewer.filename}
                  className="max-w-full max-h-[65vh] object-contain rounded"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '';
                    e.target.alt = 'Failed to load image';
                  }}
                />
              ) : (
                // Use blob URL for PDF in iframe
                <iframe
                  src={pdfViewer.blobUrl}
                  title={pdfViewer.filename}
                  className="w-full h-[65vh] rounded-lg border bg-white"
                  frameBorder="0"
                />
              )
            ) : (
              <div className="flex flex-col items-center gap-3 p-8">
                <FileText className="w-12 h-12 text-slate-300" />
                <p className="text-slate-500">No preview available</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => window.open(pdfViewer.url, '_blank')}>
              <Paperclip className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button variant="outline" onClick={() => {
              if (pdfViewer.blobUrl && !pdfViewer.blobUrl.startsWith('http')) {
                URL.revokeObjectURL(pdfViewer.blobUrl);
              }
              setPdfViewer({ open: false, url: '', filename: '', contentType: '', blobUrl: '', loading: false, error: '' });
            }}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
